import { array, boolean, discriminatedUnion, enum as enumFactory, iso, literal, never as neverFactory, number, record, strictObject, string, undefined as undefinedFactory, union, unknown } from "../../lazy-zod.js";
import { MAX_TEXT_LENGTH } from "../protocol/constants.js";
import { agentProfileIdSchema, openMeldRuntimeProfileIdSchema } from "../protocol/profile.js";
import { AGENT_STREAM_SNAPSHOT_MAX_CHARS, agentStreamToolPartSchema } from "../protocol/agent-stream.js";
import { openMeldNavigationTargetSchema } from "../../navigation-action.js";
import { messageEnvelopeSchema } from "../protocol/message-envelope.js";
import { SPACE_FILE_MAX_SIZE_BYTES, spaceMessageFileReferencesSchema } from "../http/space-files.js";
import { spaceWorkHandoffSchema } from "../../space-work-handoff.js";
import { conversationCompactionMarkerSchema } from "../protocol/conversation-compaction.js";
import { spaceMessageAttachmentSchema, spaceMessageAttachmentsSchema } from "../protocol/space-attachment.js";
                                              
import { spaceMessageReactionsSchema } from "../space-mesh/message-reaction.js";
import { centerAgentModelRouteSchema } from "./center-agent-model.js";
import { remoteAgentTurnEnvelopeSchema, remoteAgentWorkLoopCheckpointSchema } from "./remote-agent-turn-envelope.js";
import { organizationSkillRuntimeReleaseSelectionSchema } from "../http/organization-skills.js";
//#region src/schemas/runtime/remote-agent-worker.ts
const nonEmptyStringSchema = string().trim().min(1);
const optionalNonEmptyStringSchema = nonEmptyStringSchema.optional();
const nullableNonEmptyStringSchema = nonEmptyStringSchema.nullable();
const optionalDispatchErrorSummarySchema = nonEmptyStringSchema.max(512).optional();
const REMOTE_AGENT_CONVERSATION_CONTINUITY_MAX_ENTRIES = 20;
const REMOTE_AGENT_CONVERSATION_CONTINUITY_ENTRY_TEXT_MAX_CHARS = 4e3;
const REMOTE_AGENT_CONVERSATION_CONTINUITY_TOTAL_TEXT_MAX_CHARS = 16e3;
const REMOTE_AGENT_LOGICAL_CONVERSATION_ID_MAX_CHARS = 1024;
const REMOTE_AGENT_RUNTIME_CONVERSATION_ID_MAX_CHARS = 2048;
const contentHashSchema = string().regex(/^sha256:[0-9a-f]{64}$/u);
const isoDateTimeSchema = iso.datetime({ offset: true });
/** A personal Agent's Space conversation must not share a runtime with another Agent. */
function buildPersonalRemoteAgentConversationId(input) {
	return ["personal-remote-agent.v1", ...[
		input.organizationId,
		input.agentProfileId,
		input.spaceId
	].map((value) => nonEmptyStringSchema.max(256).parse(value)).map((value) => `${value.length}:${value}`)].join("|");
}
function buildRemoteAgentRuntimeConversationId(input) {
	const organizationId = nonEmptyStringSchema.max(256).parse(input.organizationId);
	const logicalConversationId = nonEmptyStringSchema.max(REMOTE_AGENT_LOGICAL_CONVERSATION_ID_MAX_CHARS).parse(input.logicalConversationId);
	const generation = number().int().nonnegative().parse(input.generation);
	if (generation === 0) return logicalConversationId;
	return nonEmptyStringSchema.max(REMOTE_AGENT_RUNTIME_CONVERSATION_ID_MAX_CHARS).parse([
		"remote-agent-runtime-generation.v1",
		`${organizationId.length}:${organizationId}`,
		`${logicalConversationId.length}:${logicalConversationId}`,
		String(generation)
	].join("|"));
}
const remoteAgentConversationContinuityEntrySourceSchema = discriminatedUnion("kind", [strictObject({
	kind: literal("space_signal"),
	signalId: nonEmptyStringSchema.max(256),
	spaceId: nonEmptyStringSchema.max(256),
	version: contentHashSchema
}), strictObject({
	conversationId: nonEmptyStringSchema.max(512),
	kind: literal("message_channel_message"),
	messageId: nonEmptyStringSchema.max(256),
	provider: enumFactory([
		"feishu",
		"lark",
		"slack"
	])
})]);
const remoteAgentConversationContinuityEntrySchema = strictObject({
	author: strictObject({
		displayName: nonEmptyStringSchema.max(256).optional(),
		id: nonEmptyStringSchema.max(256).optional(),
		kind: enumFactory([
			"agent",
			"bot",
			"human",
			"system",
			"unknown"
		])
	}),
	content: strictObject({
		materializedContentHash: contentHashSchema,
		originalCharacterCount: number().int().nonnegative(),
		text: string().max(REMOTE_AGENT_CONVERSATION_CONTINUITY_ENTRY_TEXT_MAX_CHARS * 2),
		truncated: boolean()
	}),
	reactions: spaceMessageReactionsSchema.optional(),
	occurredAt: isoDateTimeSchema,
	source: remoteAgentConversationContinuityEntrySourceSchema
}).superRefine((entry, context) => {
	const materializedCharacterCount = Array.from(entry.content.text).length;
	if (materializedCharacterCount > 4e3) context.addIssue({
		code: "custom",
		message: "Continuity entry text exceeds its character budget.",
		path: ["content", "text"]
	});
	if (materializedCharacterCount > entry.content.originalCharacterCount) context.addIssue({
		code: "custom",
		message: "Continuity entry text cannot exceed its original character count.",
		path: ["content", "originalCharacterCount"]
	});
});
const remoteAgentConversationContinuityContextSchema = strictObject({
	entries: array(remoteAgentConversationContinuityEntrySchema).min(1).max(20),
	fromGeneration: number().int().nonnegative(),
	logicalConversationId: nonEmptyStringSchema.max(REMOTE_AGENT_LOGICAL_CONVERSATION_ID_MAX_CHARS),
	toGeneration: number().int().positive(),
	truncated: boolean(),
	v: literal(1)
}).superRefine((continuity, context) => {
	if (continuity.toGeneration !== continuity.fromGeneration + 1) context.addIssue({
		code: "custom",
		message: "Continuity context must connect adjacent runtime generations.",
		path: ["toGeneration"]
	});
	if (continuity.entries.reduce((total, entry) => total + Array.from(entry.content.text).length, 0) > 16e3) context.addIssue({
		code: "custom",
		message: "Continuity context exceeds its total text budget.",
		path: ["entries"]
	});
});
const remoteAgentRuntimeConversationSchema = strictObject({
	generation: number().int().nonnegative(),
	id: nonEmptyStringSchema.max(REMOTE_AGENT_RUNTIME_CONVERSATION_ID_MAX_CHARS)
});
const REMOTE_AGENT_STREAM_DELTA_MAX_CHARS = 1024;
const REMOTE_AGENT_RUNTIME_EVIDENCE_EVENTS = {
	ADMISSION_STAGE: "remote_agent.openmeld.admission_stage",
	RPC: "remote_agent.openmeld.rpc",
	TURN: "remote_agent.openmeld.turn"
};
const REMOTE_AGENT_ADMISSION_STAGE_OPERATIONS = {
	CALLBACK_RETRY_ARM_COMPLETED: "callback_retry_arm_completed",
	CALLBACK_RETRY_ARM_STARTED: "callback_retry_arm_started",
	DURABLE_OBJECT_ENTERED: "durable_object_entered",
	DURABLE_OBJECT_EXITED: "durable_object_exited",
	FLUE_REQUEST_COMPLETED: "flue_request_completed",
	FLUE_REQUEST_STARTED: "flue_request_started",
	PROVIDER_REGISTERED: "provider_registered",
	STORAGE_INITIALIZED: "storage_initialized",
	SUBMISSION_ACKNOWLEDGED: "submission_acknowledged",
	SUBMISSION_ATTACHMENTS_RESOLVED: "submission_attachments_resolved",
	SUBMISSION_DEDUPE_COMPLETED: "submission_dedupe_completed",
	SUBMISSION_REQUEST_DIGESTED: "submission_request_digested",
	SUBMISSION_STARTED: "submission_started"
};
const remoteAgentAdmissionStageOperationSchema = enumFactory(Object.values(REMOTE_AGENT_ADMISSION_STAGE_OPERATIONS));
const REMOTE_AGENT_RPC_OPERATIONS = {
	CANCEL_EXECUTION: "cancelExecution",
	RECEIVE_EXECUTION_INPUT: "receiveExecutionInput",
	READ_EXECUTION_INPUT: "readExecutionInput",
	READ_EXECUTION_STATE: "readExecutionState",
	RECONCILE_EXECUTION: "reconcileExecution",
	READ_RUNTIME_DIAGNOSTICS: "readRuntimeDiagnostics",
	READ_USAGE_DIAGNOSTICS: "readUsageDiagnostics",
	SUBMIT_CONVERSATION_TURN: "submitConversationTurn",
	SUMMARIZE_AGENT_ACTIVITY_DIGEST: "summarizeAgentActivityDigest"
};
const remoteAgentRpcOperationSchema = enumFactory(Object.values(REMOTE_AGENT_RPC_OPERATIONS));
const REMOTE_AGENT_RUNTIME_EVIDENCE_REASON_CODES = {
	ADMISSION_ACCEPTED: "remote_agent.admission.accepted",
	ADMISSION_CONVERSATION_BUSY: "remote_agent.admission.conversation_busy",
	ADMISSION_NOT_ACCEPTED: "remote_agent.admission.not_accepted",
	PROVIDER_AUTHENTICATION_FAILED: "remote_agent.provider.authentication_failed",
	PROVIDER_FAILED: "remote_agent.provider.failed",
	PROVIDER_RATE_LIMITED: "remote_agent.provider.rate_limited",
	PROVIDER_UNAVAILABLE: "remote_agent.provider.unavailable",
	RPC_FAILED: "remote_agent.rpc.failed",
	RPC_INVALID_REQUEST: "remote_agent.rpc.invalid_request",
	RPC_SUCCEEDED: "remote_agent.rpc.succeeded",
	TURN_COMPLETED: "remote_agent.turn.completed",
	TURN_CONTEXT_WINDOW_EXCEEDED: "remote_agent.turn.context_window_exceeded",
	TURN_EMPTY_ASSISTANT_RESPONSE: "remote_agent.empty_assistant_response",
	TURN_FAILED: "remote_agent.turn.failed",
	TURN_NOT_COMPLETED: "remote_agent.turn_not_completed",
	TURN_TIMEOUT: "remote_agent.turn.timeout"
};
const remoteAgentRuntimeEvidenceReasonCodeSchema = enumFactory(Object.values(REMOTE_AGENT_RUNTIME_EVIDENCE_REASON_CODES));
const remoteAgentWorkerSubmissionStatusSchema = enumFactory([
	"pending",
	"running",
	"completed",
	"aborted",
	"skipped",
	"error"
]);
const remoteAgentWorkerSubmitMessagesResultSchema = strictObject({
	submissionId: nonEmptyStringSchema,
	idempotencyKey: optionalNonEmptyStringSchema,
	requestId: optionalNonEmptyStringSchema,
	status: remoteAgentWorkerSubmissionStatusSchema,
	error: optionalNonEmptyStringSchema,
	metadata: record(string(), unknown()).optional(),
	createdAt: number().finite(),
	startedAt: number().finite().optional(),
	completedAt: number().finite().optional(),
	accepted: boolean()
});
const remoteAgentWorkerAdmissionRejectionCodeSchema = enumFactory([
	"remote_agent.conversation_history_limit_exceeded",
	"remote_agent.routine_identity_mismatch",
	"remote_agent.member_research_identity_mismatch",
	"remote_agent.turn_envelope_identity_mismatch"
]);
const remoteAgentRuntimeEvidenceOutcomeReasonCodeSchema = union([remoteAgentRuntimeEvidenceReasonCodeSchema, remoteAgentWorkerAdmissionRejectionCodeSchema]);
const remoteAgentWorkerAdmissionRejectedResponseSchema = strictObject({
	idempotencyKey: nonEmptyStringSchema,
	message: nonEmptyStringSchema,
	outcome: literal("rejected"),
	reasonCode: remoteAgentWorkerAdmissionRejectionCodeSchema,
	v: literal(1)
});
const remoteAgentWorkerSubmitTurnResponseSchema = union([remoteAgentWorkerSubmitMessagesResultSchema, remoteAgentWorkerAdmissionRejectedResponseSchema]);
const remoteAgentWorkerInputAttachmentSchema = union([spaceMessageAttachmentSchema.extend({
	objectKey: undefinedFactory().optional(),
	removeAfterAdmission: literal(true).optional()
}), spaceMessageAttachmentSchema.extend({
	/**
	* Core-resolved immutable object key for a canonical Space File revision.
	*/
	objectKey: nonEmptyStringSchema.max(4096),
	removeAfterAdmission: neverFactory().optional(),
	sizeBytes: number().int().finite().min(1).max(SPACE_FILE_MAX_SIZE_BYTES)
})]);
const remoteAgentWorkerInputAttachmentsSchema = array(remoteAgentWorkerInputAttachmentSchema).max(10).superRefine((attachments, context) => {
	if (attachments.filter((attachment) => attachment.objectKey === void 0).length > 4) context.addIssue({
		code: "custom",
		path: [],
		message: `Legacy message attachments cannot exceed 4.`
	});
});
const remoteAgentWorkerTurnMessageSchema = strictObject({
	role: enumFactory(["user"]),
	content: nonEmptyStringSchema,
	attachments: remoteAgentWorkerInputAttachmentsSchema.optional()
});
const remoteAgentWorkerAuthorContextSchema = strictObject({
	profileId: openMeldRuntimeProfileIdSchema.nullable(),
	profileKind: enumFactory([
		"human",
		"agent",
		"system"
	]).nullable(),
	displayName: nullableNonEmptyStringSchema
});
const remoteAgentWorkerSubmitTurnRequestShape = {
	replyRecovery: strictObject({
		originalDispatchId: nonEmptyStringSchema,
		operationId: nonEmptyStringSchema,
		resultRef: nonEmptyStringSchema.optional()
	}).optional(),
	organizationId: nonEmptyStringSchema,
	spaceId: nonEmptyStringSchema,
	conversationId: optionalNonEmptyStringSchema,
	agentProfileId: agentProfileIdSchema,
	agentProfileOwnerUserId: nonEmptyStringSchema.nullable(),
	bindingId: nonEmptyStringSchema,
	triggerMessageId: nonEmptyStringSchema,
	dispatchId: nonEmptyStringSchema,
	sourceClientMessageId: nullableNonEmptyStringSchema.optional(),
	idempotencyKey: nonEmptyStringSchema,
	toolExecutionDeadlineAtMs: number().int().positive().max(Number.MAX_SAFE_INTEGER).optional(),
	messages: array(remoteAgentWorkerTurnMessageSchema).min(1),
	author: remoteAgentWorkerAuthorContextSchema,
	onBehalfOf: remoteAgentWorkerAuthorContextSchema.nullable(),
	workHandoff: spaceWorkHandoffSchema.optional(),
	organizationSkillReleases: array(organizationSkillRuntimeReleaseSelectionSchema).max(64).optional(),
	turnEnvelope: remoteAgentTurnEnvelopeSchema
};
const remoteAgentWorkerSubmitTurnRequestV1Schema = strictObject({
	v: literal(1),
	...remoteAgentWorkerSubmitTurnRequestShape
});
const remoteAgentWorkerSubmitTurnRequestV2Schema = strictObject({
	v: literal(2),
	...remoteAgentWorkerSubmitTurnRequestShape,
	modelRoute: centerAgentModelRouteSchema
});
const remoteAgentWorkerConversationIdentitySchema = strictObject({
	kind: literal("personal_agent"),
	agentProfileId: nonEmptyStringSchema
});
const remoteAgentWorkerSubmitTurnRequestV4Schema = strictObject({
	v: literal(4),
	...remoteAgentWorkerSubmitTurnRequestShape,
	conversationIdentity: remoteAgentWorkerConversationIdentitySchema.optional(),
	conversationId: nonEmptyStringSchema.max(REMOTE_AGENT_LOGICAL_CONVERSATION_ID_MAX_CHARS),
	continuityContext: remoteAgentConversationContinuityContextSchema.optional(),
	sourceMessageEnvelope: messageEnvelopeSchema.optional(),
	modelRoute: centerAgentModelRouteSchema,
	runtimeConversation: remoteAgentRuntimeConversationSchema,
	spaceDispatchContext: strictObject({
		dispatchChainId: nonEmptyStringSchema,
		autoRelayDepth: number().int().nonnegative(),
		visitedProfileIds: array(nonEmptyStringSchema).max(256),
		visitedProfileIdsHash: nonEmptyStringSchema
	}).optional()
}).superRefine((request, context) => {
	if (request.conversationIdentity?.kind === "personal_agent") {
		if (!request.spaceDispatchContext) context.addIssue({
			code: "custom",
			message: "Personal Agent turns require their source dispatch context.",
			path: ["spaceDispatchContext"]
		});
		if (request.conversationIdentity.agentProfileId !== request.agentProfileId || request.conversationId !== buildPersonalRemoteAgentConversationId(request)) context.addIssue({
			code: "custom",
			message: "Personal Agent conversations must match the Organization, Agent Profile, and Space.",
			path: ["conversationIdentity"]
		});
	}
	const expectedRuntimeConversationId = buildRemoteAgentRuntimeConversationId({
		generation: request.runtimeConversation.generation,
		logicalConversationId: request.conversationId,
		organizationId: request.organizationId
	});
	if (request.runtimeConversation.id !== expectedRuntimeConversationId) context.addIssue({
		code: "custom",
		message: "Runtime conversation identity must match its logical conversation generation.",
		path: ["runtimeConversation", "id"]
	});
	if (request.runtimeConversation.generation === 0 && request.continuityContext) context.addIssue({
		code: "custom",
		message: "Legacy generation zero cannot carry rollover continuity.",
		path: ["continuityContext"]
	});
	if (request.continuityContext && (request.continuityContext.logicalConversationId !== request.conversationId || request.continuityContext.toGeneration !== request.runtimeConversation.generation)) context.addIssue({
		code: "custom",
		message: "Continuity context must match the admitted logical conversation generation.",
		path: ["continuityContext"]
	});
});
const remoteAgentWorkerSubmitTurnRequestSchema = discriminatedUnion("v", [
	remoteAgentWorkerSubmitTurnRequestV1Schema,
	remoteAgentWorkerSubmitTurnRequestV2Schema,
	remoteAgentWorkerSubmitTurnRequestV4Schema
]);
const remoteAgentWorkerExecutionIdentitySchema = {
	v: literal(1),
	organizationId: nonEmptyStringSchema,
	spaceId: nonEmptyStringSchema,
	submissionId: nonEmptyStringSchema,
	idempotencyKey: nonEmptyStringSchema
};
const remoteAgentWorkerExecutionStatusSchema = enumFactory([
	"pending",
	"running",
	"cancellation_requested",
	"completed",
	"failed",
	"aborted"
]);
const remoteAgentWorkerExecutionProgressKindSchema = enumFactory([
	"admitted",
	"agent_started",
	"thinking_delta",
	"text_delta",
	"tool_started",
	"tool_completed",
	"tool_failed",
	"prompt_completed",
	"prompt_failed",
	"cancellation_requested",
	"aborted"
]);
const remoteAgentWorkerExecutionQuerySchema = strictObject({
	...remoteAgentWorkerExecutionIdentitySchema,
	conversationId: optionalNonEmptyStringSchema
});
const remoteAgentWorkerExecutionStateSchema = strictObject({
	...remoteAgentWorkerExecutionIdentitySchema,
	status: remoteAgentWorkerExecutionStatusSchema,
	acceptedAtMs: number().int().nonnegative(),
	lastProgressAtMs: number().int().nonnegative(),
	lastProgressKind: remoteAgentWorkerExecutionProgressKindSchema,
	completedAtMs: number().int().nonnegative().nullable(),
	cancellationReason: nonEmptyStringSchema.max(512).nullable()
});
const remoteAgentWorkerExecutionQueryResponseSchema = union([
	strictObject({
		status: literal("found"),
		execution: remoteAgentWorkerExecutionStateSchema
	}),
	strictObject({ status: literal("not_found") }),
	strictObject({
		status: literal("identity_mismatch"),
		reasonCode: literal("remote_agent.execution_identity_mismatch")
	})
]);
const remoteAgentWorkerCancellationRequestSchema = strictObject({
	...remoteAgentWorkerExecutionIdentitySchema,
	conversationId: optionalNonEmptyStringSchema,
	reason: nonEmptyStringSchema.max(512).optional()
});
const remoteAgentWorkerCancellationResponseSchema = union([
	strictObject({
		status: literal("cancellation_requested"),
		execution: remoteAgentWorkerExecutionStateSchema
	}),
	strictObject({
		status: literal("already_cancelled"),
		execution: remoteAgentWorkerExecutionStateSchema
	}),
	strictObject({
		status: literal("not_stoppable"),
		execution: remoteAgentWorkerExecutionStateSchema
	}),
	strictObject({ status: literal("not_found") }),
	strictObject({
		status: literal("identity_mismatch"),
		reasonCode: literal("remote_agent.execution_identity_mismatch")
	})
]);
const remoteAgentWorkerReadUsageDiagnosticsRequestSchema = strictObject({
	organizationId: optionalNonEmptyStringSchema,
	runtimeConversationId: nonEmptyStringSchema.max(REMOTE_AGENT_RUNTIME_CONVERSATION_ID_MAX_CHARS),
	spaceId: nonEmptyStringSchema
});
const remoteAgentWorkerRuntimeDiagnosticsRequestSchema = strictObject({
	runtimeConversationId: nonEmptyStringSchema.max(REMOTE_AGENT_RUNTIME_CONVERSATION_ID_MAX_CHARS),
	spaceId: nonEmptyStringSchema
});
const nullableNonNegativeIntegerSchema = number().int().nonnegative().nullable();
const remoteAgentRuntimeSubmissionDiagnosticSchema = strictObject({
	abortRequestedAtMs: nullableNonNegativeIntegerSchema,
	acceptedAtMs: number().int().nonnegative(),
	attemptCount: number().int().nonnegative(),
	attemptId: nullableNonEmptyStringSchema,
	canonicalReadyAtMs: nullableNonNegativeIntegerSchema,
	inputAppliedAtMs: nullableNonNegativeIntegerSchema,
	kind: enumFactory(["direct", "dispatch"]),
	leaseExpiresAtMs: number().int().nonnegative(),
	maxRetry: number().int().nonnegative(),
	ownerId: nullableNonEmptyStringSchema,
	recoveryRequestedAtMs: nullableNonNegativeIntegerSchema,
	sequence: number().int().positive(),
	settledAtMs: nullableNonNegativeIntegerSchema,
	startedAtMs: nullableNonNegativeIntegerSchema,
	status: enumFactory([
		"queued",
		"running",
		"terminalizing",
		"settled"
	]),
	submissionId: nonEmptyStringSchema,
	timeoutAtMs: number().int().nonnegative()
});
const remoteAgentRuntimeAttemptMarkerDiagnosticSchema = strictObject({
	attemptId: nonEmptyStringSchema,
	createdAtMs: number().int().nonnegative(),
	submissionId: nonEmptyStringSchema
});
const remoteAgentRuntimeActiveRunDiagnosticSchema = strictObject({
	createdAtMs: number().int().nonnegative(),
	id: nonEmptyStringSchema,
	name: nonEmptyStringSchema
});
const remoteAgentRuntimeFiberDiagnosticSchema = strictObject({
	completedAtMs: nullableNonNegativeIntegerSchema,
	createdAtMs: number().int().nonnegative(),
	fiberId: nonEmptyStringSchema,
	idempotencyKey: nullableNonEmptyStringSchema,
	name: nonEmptyStringSchema,
	startedAtMs: nullableNonNegativeIntegerSchema,
	status: nonEmptyStringSchema
});
const remoteAgentRuntimeScheduleDiagnosticSchema = strictObject({
	callback: nullableNonEmptyStringSchema,
	createdAtSeconds: number().int().nonnegative(),
	delayInSeconds: nullableNonNegativeIntegerSchema,
	executionStartedAtMs: nullableNonNegativeIntegerSchema,
	id: nonEmptyStringSchema,
	running: boolean(),
	timeMs: nullableNonNegativeIntegerSchema,
	type: nonEmptyStringSchema
});
const remoteAgentRuntimeCallbackContextDiagnosticSchema = strictObject({
	callbackAttempts: number().int().nonnegative(),
	callbackCompletedAt: iso.datetime({ offset: true }).nullable(),
	callbackNextAttemptAt: iso.datetime({ offset: true }).nullable(),
	callbackStatus: enumFactory([
		"completed",
		"delivery_failed",
		"failed",
		"pending",
		"rejected",
		"retryable"
	]),
	dispatchId: nonEmptyStringSchema,
	hasCompletedText: boolean(),
	lastCoreResponseReasonCode: nullableNonEmptyStringSchema,
	lastCoreResponseStatus: enumFactory([
		"accepted",
		"duplicate_rejected",
		"rejected",
		"retryable"
	]).nullable(),
	lastFailureCode: nullableNonEmptyStringSchema,
	submissionId: nonEmptyStringSchema,
	updatedAtMs: number().int().nonnegative()
});
const remoteAgentRecentToolCallDiagnosticSchema = strictObject({
	calledAtMs: number().int().nonnegative(),
	source: enumFactory(["code_mode", "direct"]),
	toolName: nonEmptyStringSchema.max(160)
});
const remoteAgentCapabilityDiscoveryDiagnosticSchema = strictObject({
	capabilityNames: array(nonEmptyStringSchema.max(160)).max(12),
	observedAtMs: number().int().nonnegative(),
	operation: enumFactory(["describe", "search"]),
	resultCount: number().int().nonnegative()
});
const remoteAgentProviderRequestDiagnosticSchema = strictObject({
	availableToolCount: number().int().nonnegative(),
	deferredToolCount: number().int().nonnegative(),
	exposedToolCount: number().int().nonnegative(),
	modelId: nullableNonEmptyStringSchema,
	preparedAtMs: number().int().nonnegative()
});
const remoteAgentTurnRuntimeEvidenceSchema = strictObject({
	budgetExhaustedAtMs: nullableNonNegativeIntegerSchema,
	capabilityDescribeCount: number().int().nonnegative(),
	capabilitySearchCount: number().int().nonnegative(),
	codeModeToolCallCount: number().int().nonnegative(),
	directToolCallCount: number().int().nonnegative(),
	firstAssistantTextAtMs: nullableNonNegativeIntegerSchema,
	firstModelEventAtMs: nullableNonNegativeIntegerSchema,
	firstProviderRequestPreparedAtMs: nullableNonNegativeIntegerSchema,
	firstProviderResponseHeadersAtMs: nullableNonNegativeIntegerSchema,
	firstVisibleContentAtMs: nullableNonNegativeIntegerSchema,
	lastModelCompletedAtMs: nullableNonNegativeIntegerSchema,
	modelCallCount: number().int().nonnegative(),
	modelId: nullableNonEmptyStringSchema,
	providerAvailableToolCount: number().int().nonnegative(),
	providerDeferredToolCount: number().int().nonnegative(),
	providerExposedToolCount: number().int().nonnegative(),
	providerRequestCount: number().int().nonnegative(),
	recentCapabilityDiscovery: array(remoteAgentCapabilityDiscoveryDiagnosticSchema).max(16),
	recentProviderRequests: array(remoteAgentProviderRequestDiagnosticSchema).max(16),
	recentToolCalls: array(remoteAgentRecentToolCallDiagnosticSchema).max(32),
	submissionId: nonEmptyStringSchema,
	toolCallCount: number().int().nonnegative(),
	toolCallLimit: number().int().positive(),
	updatedAtMs: number().int().nonnegative()
});
const remoteAgentWorkerRuntimeDiagnosticsResponseV1Schema = strictObject({
	activeRuns: array(remoteAgentRuntimeActiveRunDiagnosticSchema).max(50),
	attemptMarkers: array(remoteAgentRuntimeAttemptMarkerDiagnosticSchema).max(50),
	callbackContexts: array(remoteAgentRuntimeCallbackContextDiagnosticSchema).max(50),
	collectedAt: iso.datetime({ offset: true }),
	durableObjectAlarmAtMs: nullableNonNegativeIntegerSchema,
	fibers: array(remoteAgentRuntimeFiberDiagnosticSchema).max(50),
	schedules: array(remoteAgentRuntimeScheduleDiagnosticSchema).max(50),
	spaceId: nonEmptyStringSchema,
	submissions: array(remoteAgentRuntimeSubmissionDiagnosticSchema).max(50),
	turnObservability: array(remoteAgentTurnRuntimeEvidenceSchema).max(50).optional(),
	v: literal(1)
});
const remoteAgentWorkerRuntimeDiagnosticsResponseSchema = discriminatedUnion("v", [remoteAgentWorkerRuntimeDiagnosticsResponseV1Schema, remoteAgentWorkerRuntimeDiagnosticsResponseV1Schema.omit({
	attemptMarkers: true,
	submissions: true,
	v: true
}).extend({
	submissions: array(remoteAgentRuntimeSubmissionDiagnosticSchema.omit({
		maxRetry: true,
		recoveryRequestedAtMs: true
	}).extend({
		maxAttempts: number().int().positive(),
		joinedInto: nullableNonEmptyStringSchema,
		status: enumFactory([
			"queued",
			"running",
			"terminalizing",
			"settled",
			"joining",
			"joined"
		])
	})).max(50),
	v: literal(2)
})]);
const remoteAgentCoreCallbackIdentitySchema = {
	submissionId: nonEmptyStringSchema,
	idempotencyKey: nonEmptyStringSchema,
	organizationId: nonEmptyStringSchema,
	spaceId: nonEmptyStringSchema,
	agentProfileId: agentProfileIdSchema,
	bindingId: nonEmptyStringSchema,
	triggerMessageId: nonEmptyStringSchema,
	dispatchId: nonEmptyStringSchema,
	callbackAttempt: number().int().positive().optional()
};
const remoteAgentStreamDeltaCallbackIdentitySchema = {
	submissionId: nonEmptyStringSchema,
	idempotencyKey: nonEmptyStringSchema,
	organizationId: nonEmptyStringSchema,
	spaceId: nonEmptyStringSchema,
	agentProfileId: agentProfileIdSchema,
	bindingId: nonEmptyStringSchema,
	triggerMessageId: nonEmptyStringSchema,
	dispatchId: nonEmptyStringSchema
};
const legacyRemoteAgentStreamDeltaCallbackBodySchema = strictObject({
	v: literal(1),
	streamId: nonEmptyStringSchema,
	sequence: number().int().positive(),
	...remoteAgentStreamDeltaCallbackIdentitySchema,
	source: enumFactory(["flue.text_delta", "simulated.final_text"]),
	textDelta: string().min(1).max(REMOTE_AGENT_STREAM_DELTA_MAX_CHARS),
	textSnapshot: string().min(1).max(AGENT_STREAM_SNAPSHOT_MAX_CHARS),
	observedAtMs: number().int().nonnegative()
});
const typedRemoteAgentStreamDeltaCallbackIdentitySchema = {
	v: literal(2),
	streamId: nonEmptyStringSchema,
	sequence: number().int().positive(),
	...remoteAgentStreamDeltaCallbackIdentitySchema,
	delta: string().min(1).max(REMOTE_AGENT_STREAM_DELTA_MAX_CHARS),
	snapshot: string().min(1).max(AGENT_STREAM_SNAPSHOT_MAX_CHARS),
	observedAtMs: number().int().nonnegative()
};
const remoteAgentStreamDeltaCallbackBodySchema = union([
	legacyRemoteAgentStreamDeltaCallbackBodySchema,
	strictObject({
		...typedRemoteAgentStreamDeltaCallbackIdentitySchema,
		kind: literal("text"),
		source: literal("flue.text_delta")
	}),
	strictObject({
		...typedRemoteAgentStreamDeltaCallbackIdentitySchema,
		kind: literal("thinking"),
		source: literal("flue.thinking_delta")
	})
]);
const remoteAgentStreamPartCallbackBodySchema = strictObject({
	v: literal(1),
	streamId: nonEmptyStringSchema,
	sequence: number().int().positive(),
	...remoteAgentStreamDeltaCallbackIdentitySchema,
	source: literal("flue.tool"),
	part: agentStreamToolPartSchema,
	observedAtMs: number().int().nonnegative()
});
const remoteAgentStreamLastSequenceSchema = number().int().nonnegative();
const remoteAgentRetryAfterMsSchema = number().int().nonnegative().max(3e5);
const remoteAgentStreamDeltaCallbackResponseSchema = union([
	strictObject({
		status: literal("accepted"),
		lastSequence: remoteAgentStreamLastSequenceSchema,
		gapDetected: boolean().optional()
	}),
	strictObject({
		status: literal("duplicate_accepted"),
		lastSequence: remoteAgentStreamLastSequenceSchema
	}),
	strictObject({
		status: literal("rejected"),
		lastSequence: remoteAgentStreamLastSequenceSchema,
		reasonCode: nonEmptyStringSchema
	}),
	strictObject({
		status: literal("retryable"),
		lastSequence: remoteAgentStreamLastSequenceSchema,
		reasonCode: nonEmptyStringSchema,
		retryAfterMs: remoteAgentRetryAfterMsSchema.optional()
	})
]);
const remoteAgentCoreCallbackBodySchema = union([strictObject({
	...remoteAgentCoreCallbackIdentitySchema,
	conversationCompaction: conversationCompactionMarkerSchema.optional(),
	navigationTarget: openMeldNavigationTargetSchema.optional(),
	status: literal("completed").optional(),
	mentionTargets: array(openMeldRuntimeProfileIdSchema).max(25).optional(),
	text: nonEmptyStringSchema.max(MAX_TEXT_LENGTH),
	attachments: spaceMessageAttachmentsSchema.optional(),
	files: spaceMessageFileReferencesSchema.optional()
}), strictObject({
	...remoteAgentCoreCallbackIdentitySchema,
	status: literal("failed"),
	failureCode: nonEmptyStringSchema,
	failureDetail: optionalDispatchErrorSummarySchema,
	failureMessage: nonEmptyStringSchema
})]);
const remoteAgentCoreWorkLoopContinuationCallbackBodySchema = strictObject({
	...remoteAgentCoreCallbackIdentitySchema,
	checkpoint: remoteAgentWorkLoopCheckpointSchema,
	status: literal("continuation_required")
});
const remoteAgentCoreCallbackEventSchema = union([remoteAgentCoreCallbackBodySchema, remoteAgentCoreWorkLoopContinuationCallbackBodySchema]);
const routineWorkflowContinuationEventPayloadSchema = discriminatedUnion("kind", [strictObject({
	callback: remoteAgentCoreCallbackBodySchema,
	kind: literal("callback")
}), strictObject({
	failureCode: nonEmptyStringSchema,
	kind: literal("terminal_failure")
})]);
const remoteAgentCoreCompletedCallbackBodySchema = strictObject({
	...remoteAgentCoreCallbackIdentitySchema,
	conversationCompaction: conversationCompactionMarkerSchema.optional(),
	navigationTarget: openMeldNavigationTargetSchema.optional(),
	status: literal("completed"),
	text: nonEmptyStringSchema.max(MAX_TEXT_LENGTH),
	attachments: spaceMessageAttachmentsSchema.optional(),
	files: spaceMessageFileReferencesSchema.optional()
});
const remoteAgentCoreFailedCallbackBodySchema = strictObject({
	...remoteAgentCoreCallbackIdentitySchema,
	status: literal("failed"),
	failureCode: nonEmptyStringSchema,
	failureDetail: optionalDispatchErrorSummarySchema,
	failureMessage: nonEmptyStringSchema
});
const remoteAgentCoreCallbackResponseSchema = strictObject({
	status: enumFactory([
		"accepted",
		"duplicate_rejected",
		"rejected",
		"retryable"
	]),
	reasonCode: nonEmptyStringSchema.optional(),
	publishedSignalId: nonEmptyStringSchema.nullable().optional(),
	retryAfterMs: remoteAgentRetryAfterMsSchema.optional()
});
//#endregion
export { REMOTE_AGENT_ADMISSION_STAGE_OPERATIONS, REMOTE_AGENT_CONVERSATION_CONTINUITY_ENTRY_TEXT_MAX_CHARS, REMOTE_AGENT_CONVERSATION_CONTINUITY_MAX_ENTRIES, REMOTE_AGENT_CONVERSATION_CONTINUITY_TOTAL_TEXT_MAX_CHARS, REMOTE_AGENT_RPC_OPERATIONS, REMOTE_AGENT_RUNTIME_EVIDENCE_EVENTS, REMOTE_AGENT_RUNTIME_EVIDENCE_REASON_CODES, REMOTE_AGENT_STREAM_DELTA_MAX_CHARS, buildPersonalRemoteAgentConversationId, buildRemoteAgentRuntimeConversationId, remoteAgentAdmissionStageOperationSchema, remoteAgentConversationContinuityContextSchema, remoteAgentConversationContinuityEntrySchema, remoteAgentConversationContinuityEntrySourceSchema, remoteAgentCoreCallbackBodySchema, remoteAgentCoreCallbackEventSchema, remoteAgentCoreCallbackResponseSchema, remoteAgentCoreCompletedCallbackBodySchema, remoteAgentCoreFailedCallbackBodySchema, remoteAgentCoreWorkLoopContinuationCallbackBodySchema, remoteAgentRpcOperationSchema, remoteAgentRuntimeConversationSchema, remoteAgentRuntimeEvidenceOutcomeReasonCodeSchema, remoteAgentRuntimeEvidenceReasonCodeSchema, remoteAgentStreamDeltaCallbackBodySchema, remoteAgentStreamDeltaCallbackResponseSchema, remoteAgentStreamPartCallbackBodySchema, remoteAgentTurnRuntimeEvidenceSchema, remoteAgentWorkerAdmissionRejectedResponseSchema, remoteAgentWorkerAdmissionRejectionCodeSchema, remoteAgentWorkerAuthorContextSchema, remoteAgentWorkerCancellationRequestSchema, remoteAgentWorkerCancellationResponseSchema, remoteAgentWorkerConversationIdentitySchema, remoteAgentWorkerExecutionProgressKindSchema, remoteAgentWorkerExecutionQueryResponseSchema, remoteAgentWorkerExecutionQuerySchema, remoteAgentWorkerExecutionStateSchema, remoteAgentWorkerExecutionStatusSchema, remoteAgentWorkerInputAttachmentSchema, remoteAgentWorkerInputAttachmentsSchema, remoteAgentWorkerReadUsageDiagnosticsRequestSchema, remoteAgentWorkerRuntimeDiagnosticsRequestSchema, remoteAgentWorkerRuntimeDiagnosticsResponseSchema, remoteAgentWorkerSubmissionStatusSchema, remoteAgentWorkerSubmitMessagesResultSchema, remoteAgentWorkerSubmitTurnRequestSchema, remoteAgentWorkerSubmitTurnRequestV1Schema, remoteAgentWorkerSubmitTurnRequestV2Schema, remoteAgentWorkerSubmitTurnRequestV4Schema, remoteAgentWorkerSubmitTurnResponseSchema, remoteAgentWorkerTurnMessageSchema, routineWorkflowContinuationEventPayloadSchema };

//# sourceMappingURL=remote-agent-worker.js.map